import { Timestamp } from "firebase/firestore";

/**
 * CTribe Kanban Board - ClickUp-Style Project Management
 * Data models for Firestore collections
 */

// ============================================================================
// Enums & Constants
// ============================================================================

export type TaskPriority = "urgent" | "high" | "normal" | "low";

export type TaskStatus = "pending" | "in_progress" | "review" | "completed";

export type WorkspaceMemberRole = "owner" | "admin" | "member" | "guest";

export type WorkspaceType = "internal" | "partner";

export type ViewType = "kanban" | "list" | "calendar";

export const TASK_PRIORITIES = ["urgent", "high", "normal", "low"] as const;

export const PRIORITY_COLORS: Record<TaskPriority, string> = {
  urgent: "#dc2626", // red-600
  high: "#f97316", // orange-500
  normal: "#3b82f6", // blue-500
  low: "#6b7280", // gray-500
};

export const DEFAULT_STATUSES: KanbanStatus[] = [
  { id: "todo", name: "To Do", color: "#6b7280", order: 0 },
  { id: "in_progress", name: "In Progress", color: "#3b82f6", order: 1 },
  { id: "review", name: "Review", color: "#f59e0b", order: 2 },
  { id: "done", name: "Done", color: "#22c55e", order: 3 },
];

// ============================================================================
// Kanban Status (Column Definition)
// ============================================================================

export interface KanbanStatus {
  id: string;
  name: string;
  color: string;
  order: number;
}

// ============================================================================
// Custom Field Definitions
// ============================================================================

export type CustomFieldType = "text" | "number" | "date" | "dropdown" | "checkbox" | "url";

export interface CustomFieldOption {
  id: string;
  label: string;
  color?: string;
}

export interface CustomFieldDefinition {
  id: string;
  name: string;
  type: CustomFieldType;
  required?: boolean;
  options?: CustomFieldOption[]; // For dropdown type
}

// ============================================================================
// Workspace (Collection: kanban_workspaces)
// ============================================================================

export interface WorkspaceMember {
  userId: string;
  email: string;
  displayName: string;
  photoUrl?: string;
  role: WorkspaceMemberRole;
  addedAt: Timestamp;
  addedBy: string;
}

export interface WorkspaceSettings {
  defaultView: ViewType;
  customFields: CustomFieldDefinition[];
  defaultStatuses: KanbanStatus[];
  allowGuestAccess: boolean;
  notificationsEnabled: boolean;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  description?: string;
  type: WorkspaceType;
  iconUrl?: string;
  color?: string;

  // Embedded for fast access
  members: WorkspaceMember[];
  // Denormalized array of user IDs for efficient Firestore security rules queries
  member_ids: string[];
  // Owner ID for workspace-level permission checks
  owner_id: string;
  settings: WorkspaceSettings;

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

// ============================================================================
// Space (Subcollection: kanban_workspaces/{workspaceId}/spaces)
// ============================================================================

export interface Space {
  id: string;
  workspaceId: string;
  name: string;
  slug: string;
  description?: string;
  iconUrl?: string;
  color?: string;
  order: number;

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

// ============================================================================
// List (Subcollection: kanban_workspaces/{workspaceId}/spaces/{spaceId}/lists)
// ============================================================================

export interface List {
  id: string;
  workspaceId: string;
  spaceId: string;
  name: string;
  slug: string;
  description?: string;
  statuses: KanbanStatus[];
  order: number;
  taskCount: number;

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

// ============================================================================
// Task Assignee
// ============================================================================

export interface TaskAssignee {
  userId: string;
  email: string;
  displayName: string;
  photoUrl?: string;
  assignedAt: Timestamp;
  assignedBy: string;
}

// ============================================================================
// Task (Collection: kanban_tasks - root level for cross-workspace queries)
// ============================================================================

export interface Task {
  id: string;

  // Location references
  workspaceId: string;
  spaceId: string;
  listId: string;

  // Core fields
  title: string;
  description?: string;
  statusId: string;
  priority: TaskPriority;

  // Assignments
  assignees: TaskAssignee[];
  watchers: string[]; // User IDs

  // Scheduling
  dueDate?: Timestamp;
  startDate?: Timestamp;

  // Organization
  tags: string[];
  customFields: Record<string, unknown>;

  // Hierarchy
  parentTaskId?: string; // For subtasks
  subtaskCount: number;
  subtaskCompletedCount: number;

  // Activity
  commentCount: number;
  attachmentCount: number;

  // Goal / OKR linking
  goalId?: string;        // Goal this task contributes to
  keyResultId?: string;   // Specific Key Result this task supports

  // Ordering within status column
  orderInStatus: number;

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
  completedAt?: Timestamp;
  completedBy?: string;
}

// ============================================================================
// Task Comment (Subcollection: kanban_tasks/{taskId}/comments)
// ============================================================================

export interface TaskCommentMention {
  userId: string;
  displayName: string;
  startIndex: number;
  endIndex: number;
}

export interface TaskComment {
  id: string;
  taskId: string;
  content: string;
  mentions: TaskCommentMention[];
  attachmentUrls?: string[];

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  creatorDisplayName?: string;
  updatedAt?: Timestamp;
  editedAt?: Timestamp;
}

// ============================================================================
// Task Attachment (Subcollection: kanban_tasks/{taskId}/attachments)
// ============================================================================

export interface TaskAttachment {
  id: string;
  taskId: string;
  fileName: string;
  fileUrl: string;
  filePath?: string;
  fileSize: number;
  mimeType: string;

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
}

// ============================================================================
// Workspace Invite (Subcollection: kanban_workspaces/{workspaceId}/invites)
// ============================================================================

export interface WorkspaceInvite {
  id: string;
  workspaceId: string;
  email: string;
  displayName: string;
  role: WorkspaceMemberRole;
  inviterId: string;
  inviterName: string;
  status: "pending" | "accepted" | "declined";
  createdAt: Timestamp;
  acceptedById?: string;
  acceptedByEmail?: string;
  acceptedByName?: string;
  acceptedAt?: Timestamp;
  declinedAt?: Timestamp;
  updatedAt?: Timestamp;
  processedAt?: Timestamp;
}

// ============================================================================
// Task Activity Log (Subcollection: kanban_tasks/{taskId}/activity)
// ============================================================================

export type TaskActivityType =
  | "created"
  | "status_changed"
  | "priority_changed"
  | "assignee_added"
  | "assignee_removed"
  | "due_date_changed"
  | "title_changed"
  | "description_changed"
  | "comment_added"
  | "attachment_added"
  | "subtask_added"
  | "subtask_completed"
  | "moved"
  | "goal_linked"
  | "goal_unlinked";

export interface TaskActivity {
  id: string;
  taskId: string;
  type: TaskActivityType;
  userId: string;
  userDisplayName: string;

  // Change details
  previousValue?: string;
  newValue?: string;
  metadata?: Record<string, unknown>;

  // Metadata
  createdAt: Timestamp;
}

// ============================================================================
// Clawdbot Integration Types
// ============================================================================

export type ClawdbotTaskIntent =
  | "task.create"
  | "task.update"
  | "task.complete"
  | "task.list"
  | "task.assign"
  | "task.comment"
  | "board.summary"
  | "space.create"
  | "list.create";

export interface ClawdbotTaskRequest {
  intent: ClawdbotTaskIntent;
  entities: {
    workspaceSlug?: string;
    spaceSlug?: string;
    listSlug?: string;
    taskId?: string;
    taskTitle?: string;
    taskDescription?: string;
    assigneeMentions?: string[];
    priority?: TaskPriority;
    dueDate?: string; // ISO string
    statusId?: string;
    comment?: string;
    filter?: "my" | "all" | "overdue" | "today";
    spaceName?: string;
    listName?: string;
    goalId?: string;
    keyResultId?: string;
  };
  userId: string;
  userDisplayName: string;
  channel: "discord" | "slack" | "api";
  channelUserId?: string;
}

export interface ClawdbotTaskResponse {
  success: boolean;
  message: string;
  data?: {
    task?: Task;
    tasks?: Task[];
    space?: Space;
    list?: List;
    summary?: {
      totalTasks: number;
      byStatus: Record<string, number>;
      overdue: number;
      dueToday: number;
    };
  };
  error?: string;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface CreateTaskRequest {
  workspaceId: string;
  spaceId: string;
  listId: string;
  title: string;
  description?: string;
  priority?: TaskPriority;
  statusId?: string;
  assigneeIds?: string[];
  dueDate?: string; // ISO string
  tags?: string[];
  parentTaskId?: string;
  goalId?: string;
  keyResultId?: string;
}

export interface UpdateTaskRequest {
  taskId: string;
  title?: string;
  description?: string;
  priority?: TaskPriority;
  statusId?: string;
  assigneeIds?: string[];
  dueDate?: string | null; // ISO string or null to clear
  tags?: string[];
  customFields?: Record<string, unknown>;
  goalId?: string | null;
  keyResultId?: string | null;
}

export interface MoveTaskRequest {
  taskId: string;
  targetStatusId: string;
  targetOrder: number;
  targetListId?: string; // For moving between lists
}

export interface TaskQueryFilters {
  workspaceId?: string;
  spaceId?: string;
  listId?: string;
  statusId?: string;
  priority?: TaskPriority;
  assigneeId?: string;
  dueBefore?: string; // ISO string
  dueAfter?: string; // ISO string
  createdBy?: string;
  tags?: string[];
  parentTaskId?: string | null; // null = root tasks only
  searchQuery?: string;
}

export interface PaginatedTasksRequest {
  filters: TaskQueryFilters;
  limit?: number;
  cursor?: string; // Last task ID for cursor-based pagination
  orderBy?: "dueDate" | "createdAt" | "priority" | "orderInStatus";
  orderDirection?: "asc" | "desc";
}

export interface PaginatedTasksResponse {
  tasks: Task[];
  hasMore: boolean;
  nextCursor?: string;
  totalCount?: number;
}

// ============================================================================
// View State Types (for UI)
// ============================================================================

export interface KanbanViewState {
  workspaceId: string;
  spaceId: string;
  listId: string;
  filters: TaskQueryFilters;
  groupBy: "status" | "priority" | "assignee";
  sortBy: "orderInStatus" | "dueDate" | "priority" | "createdAt";
  sortDirection: "asc" | "desc";
  showSubtasks: boolean;
  showCompletedTasks: boolean;
}

export interface TasksByStatus {
  [statusId: string]: Task[];
}

// ============================================================================
// Helper Type Guards
// ============================================================================

export function isValidPriority(value: string): value is TaskPriority {
  return TASK_PRIORITIES.includes(value as TaskPriority);
}

export function isValidWorkspaceMemberRole(value: string): value is WorkspaceMemberRole {
  return ["owner", "admin", "member", "guest"].includes(value);
}

export function isValidViewType(value: string): value is ViewType {
  return ["kanban", "list", "calendar"].includes(value);
}

export function isValidWorkspaceType(value: string): value is WorkspaceType {
  return ["internal", "partner"].includes(value);
}

// ============================================================================
// Utility Functions
// ============================================================================

export function getDefaultWorkspaceSettings(): WorkspaceSettings {
  return {
    defaultView: "kanban",
    customFields: [],
    defaultStatuses: [...DEFAULT_STATUSES],
    allowGuestAccess: false,
    notificationsEnabled: true,
  };
}

export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function getPriorityOrder(priority: TaskPriority): number {
  const order: Record<TaskPriority, number> = {
    urgent: 0,
    high: 1,
    normal: 2,
    low: 3,
  };
  return order[priority];
}

export function sortTasksByPriority(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => getPriorityOrder(a.priority) - getPriorityOrder(b.priority));
}
